import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import './SearchableSelect.css';

/**
 * A single-choice picker that stays usable as the list grows.
 *
 * Rows of buttons are fine for a handful of fixed options (months, say), but
 * anything sourced from a workspace's own data — dealers, brands, users — has
 * no ceiling, and a dozen already wraps onto a second line. This keeps the
 * control one line wide however many options there are, and brings up a
 * search box once there are enough to be worth filtering.
 *
 * Props:
 *   value, onChange   the selected option's value ('' means the "all" row)
 *   options           [{ value, label }]
 *   allLabel          the always-present first row, e.g. "All Dealers"
 *   loading           shows a spinner in the button while a fetch is in flight
 *   searchFrom        how many options before the search box appears
 *   className etc.    styling hooks, so a host page can match its own look
 */
export function SearchableSelect({
  value,
  onChange,
  options = [],
  allLabel = 'All',
  placeholder = 'Search…',
  loading = false,
  searchFrom = 8,
  className = '',
  buttonClassName = 'input text-sm',
  spinner = null,
  emptyLabel = 'No matches',
}) {
  const [open, setOpen]       = useState(false);
  const [search, setSearch]   = useState('');
  const [cursor, setCursor]   = useState(0);
  const wrapRef   = useRef(null);
  const panelRef  = useRef(null);
  const listRef   = useRef(null);
  const searchRef = useRef(null);
  // The panel is rendered into document.body rather than next to the button.
  // Several of these pickers live inside horizontally scrolling tables, and a
  // panel positioned inside one of those gets clipped by the scroll container
  // — so it is positioned against the viewport instead.
  const [rect, setRect] = useState(null);

  const selected = options.find(o => String(o.value) === String(value));
  const showSearch = options.length >= searchFrom;

  // "All" is an option like any other here, so one list handles both the
  // keyboard cursor and the click targets.
  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const matched = term
      ? options.filter(o => o.label.toLowerCase().includes(term))
      : options;
    return [{ value: '', label: allLabel }, ...matched];
  }, [options, search, allLabel]);

  // Where to put the panel: under the button, or above it when the space below
  // is too tight (a picker on the last row of a table).
  const place = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const below = window.innerHeight - r.bottom;
    const flip  = below < 300 && r.top > below;
    setRect({ left: r.left, top: r.bottom + 4, bottom: window.innerHeight - r.top + 4, width: r.width, flip });
  }, []);

  useLayoutEffect(() => { if (open) place(); }, [open, place]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    // Follow the button if anything scrolls — including the table it sits in,
    // hence the capture phase rather than a window listener alone.
    document.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, place]);

  // Opening lands the cursor on whatever is currently selected, so Enter
  // without moving is a no-op rather than a surprise.
  useEffect(() => {
    if (!open) return;
    setSearch('');
    const at = rows.findIndex(r => String(r.value) === String(value));
    setCursor(at >= 0 ? at : 0);
    if (showSearch) setTimeout(() => searchRef.current?.focus(), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Keep the highlighted row in view when arrowing through a long list.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-row="${cursor}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [cursor, open]);

  const pick = (val) => { onChange(val); setOpen(false); };

  const onKeyDown = (e) => {
    if (!open) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); }
      return;
    }
    if (e.key === 'Escape')      { e.preventDefault(); setOpen(false); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); setCursor(c => Math.min(c + 1, rows.length - 1)); }
    else if (e.key === 'ArrowUp')   { e.preventDefault(); setCursor(c => Math.max(c - 1, 0)); }
    else if (e.key === 'Home')      { e.preventDefault(); setCursor(0); }
    else if (e.key === 'End')       { e.preventDefault(); setCursor(rows.length - 1); }
    else if (e.key === 'Enter')     { e.preventDefault(); if (rows[cursor]) pick(rows[cursor].value); }
  };

  return (
    <div ref={wrapRef} className={`ss-wrap ${className}`} onKeyDown={onKeyDown}>
      <button
        type="button"
        className={buttonClassName}
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {loading && spinner}
        <span className="ss-value">{selected ? selected.label : allLabel}</span>
        <ChevronDownIcon className="ss-chevron" aria-hidden="true" />
      </button>

      {open && rect && createPortal(
        <div
          ref={panelRef}
          className="ss-panel"
          role="listbox"
          /* No key handler here: a portal still bubbles events up the React
             tree, so the wrapper's handler already sees keys pressed inside
             the panel. Attaching one here ran it twice per press. */
          style={{
            position: 'fixed',
            left: rect.left,
            ...(rect.flip ? { bottom: rect.bottom } : { top: rect.top }),
            minWidth: Math.max(rect.width, 240),
          }}
        >
          {showSearch && (
            <div className="ss-search">
              <MagnifyingGlassIcon className="ss-search-icon" aria-hidden="true" />
              <input
                ref={searchRef}
                type="text"
                value={search}
                placeholder={placeholder}
                onChange={e => { setSearch(e.target.value); setCursor(0); }}
                aria-label={placeholder}
              />
            </div>
          )}

          <div className="ss-list" ref={listRef}>
            {rows.length === 1 && search ? (
              <div className="ss-empty">{emptyLabel}</div>
            ) : (
              rows.map((o, i) => (
                <button
                  key={`${o.value}-${i}`}
                  type="button"
                  data-row={i}
                  role="option"
                  aria-selected={String(o.value) === String(value)}
                  className={[
                    'ss-option',
                    String(o.value) === String(value) ? 'selected' : '',
                    i === cursor ? 'cursor' : '',
                    o.value === '' ? 'all' : '',
                  ].filter(Boolean).join(' ')}
                  onMouseEnter={() => setCursor(i)}
                  onClick={() => pick(o.value)}
                >
                  {o.label}
                </button>
              ))
            )}
          </div>

          {showSearch && (
            <div className="ss-foot">
              {rows.length - 1} of {options.length}
            </div>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}

export default SearchableSelect;
